/* global Element, MutationObserver */
import { RefObject, useLayoutEffect, useRef } from 'react';

import {
  ActionMirrorSource, MirroredAction, actionMirrorScope, buttonIsDisabled, removeMirrorRegion, updateMirrorRegion,
} from './actionMirrorStore';

const MIRROR = '[data-sticky-action-mirror]';
const BAR = '.ai-overview-action-bar';
const BUTTON = 'button.ai-overview-action-btn, button.agent-suggestion-pill';
const excludedBar = (bar: HTMLElement): boolean => Boolean(bar.closest(MIRROR))
  || bar.dataset.actionMirrorOwner === 'historical'
  || bar.matches('.ai-overview-action-bar--historical, .ai-overview-action-bar--hidden, .ai-overview-action-bar--attention-superseded');
const suppressedSource = (element: HTMLElement): boolean => element.matches(BAR)
  && element.hasAttribute('data-action-source-selected') && element.style.display !== 'none';
const isVisible = (element: HTMLElement): boolean => {
  let current: HTMLElement | null = element;

  while (current) {
    const style = window.getComputedStyle(current);
    const overflowed = current === element && current.dataset.actionOverflowHidden === 'true';

    if (current.hidden || current.getAttribute('aria-hidden') === 'true' || style.visibility === 'hidden'
      || (style.display === 'none' && !overflowed && !suppressedSource(current))) return false;
    current = current.parentElement;
  }

  return true;
};
const readAction = (button: HTMLButtonElement): MirroredAction => ({
  button,
  label: (button.querySelector('.ai-overview-action-btn__label, .agent-suggestion-pill__label')?.textContent
    || button.textContent || button.getAttribute('aria-label') || button.title || 'Action').replace(/\s+/g, ' ').trim(),
  title: button.title,
  disabled: buttonIsDisabled(button),
  busy: button.getAttribute('aria-busy') === 'true'
    || button.matches('.ai-overview-action-btn--continuing, .agent-suggestion-pill--loading'),
  selected: button.getAttribute('aria-pressed') === 'true'
    || button.matches('.ai-overview-action-btn--selected, .ai-overview-action-btn--done, .agent-suggestion-pill--triggered'),
  attentionPrimary: button.dataset.attentionPrimary === 'true',
  placement: button.dataset.actionPlacement === 'pinned' || button.dataset.actionPlacement === 'secondary'
    ? button.dataset.actionPlacement : button.matches('.ai-overview-action-btn--redo') ? 'secondary' : undefined,
  iconClass: button.querySelector('i')?.className ?? null,
  stateClasses: Array.from(button.classList).filter((name) => /^(ai-overview-action-btn|agent-suggestion-pill)--/.test(name)).join(' '),
});
const ownerLabel = (bar: HTMLElement): string => {
  if (bar.dataset.actionOwnerLabel) return bar.dataset.actionOwnerLabel;
  const wrapper = bar.closest('.ai-overview-item-wrapper, .planning-failure-banner, .failed-task-recovery');
  const heading = wrapper?.querySelector('.message-identity-label, .ai-overview-artifact-card__label, .ai-overview-bubble__label');

  return heading?.textContent?.replace(/\s+/g, ' ').trim().slice(0, 90) || 'Latest message';
};
const regionBars = (root: HTMLElement): HTMLElement[] => (
  Array.from(root.querySelectorAll<HTMLElement>(BAR)).filter((bar) => !bar.closest(MIRROR))
);

const readSource = (bar: HTMLElement): ActionMirrorSource | null => {
  if (excludedBar(bar) || !isVisible(bar)) return null;
  const actions = Array.from(bar.querySelectorAll<HTMLButtonElement>(BUTTON)).filter((button) => (
    button.closest(BAR) === bar && !button.closest('[data-action-overflow-control]')
    && (!button.closest('.ai-overview-action-bar__utilities') || button.matches('.ai-overview-action-btn--redo'))
    && !button.matches('.ai-overview-more-actions__trigger') && button.dataset.actionMirror !== 'skip'
    && isVisible(button)
  )).map(readAction);
  const current = bar.dataset.actionMirrorOwner === 'current' || bar.dataset.awaitingUser === 'true';

  // Undo alone does not own a workflow decision.
  if (!actions.some((action) => !action.button.matches('.ai-overview-action-btn--undo')
    && (!action.disabled || action.busy || (current
      && !action.button.matches('.ai-overview-action-btn--done, .agent-suggestion-pill--triggered'))))) return null;

  const retryable = current && actions.some((action) => (
    action.button.dataset.actionRetryable === 'true' && (!action.disabled || action.busy)
  ));

  return { element: bar, ownerId: bar.dataset.actionSourceEntryId || '', ownerLabel: ownerLabel(bar), actions, current,
    retryable,
    inlineAtTail: bar.dataset.actionInlineTail === 'true',
    errors: Array.from(bar.querySelectorAll<HTMLElement>('[role="alert"]'))
      .filter((alert) => alert.closest(BAR) === bar && isVisible(alert))
      .map((alert) => alert.textContent?.trim() || '').filter(Boolean),
    // Hidden sources have no geometry; the sticky copy owns the cue's timer.
    attentionActive: Boolean(bar.querySelector('[data-action-attention-active="true"]')) };
};
// Undo, Redo and Share are conveniences, not decisions.
const PASSIVE_ACTION = '.ai-overview-action-btn--undo, .ai-overview-action-btn--redo, .ai-overview-action-btn--share';
const isPassiveSource = (source: ActionMirrorSource): boolean => (
  source.actions.every((action) => action.button.matches(PASSIVE_ACTION))
);
/** An open gate keeps the dock; the newer entry's Undo/Redo ride along with it. */
const withNewerUtilities = (gate: ActionMirrorSource, newer: ActionMirrorSource): ActionMirrorSource => ({
  ...gate, actions: [...gate.actions, ...newer.actions.filter((action) => (
    action.button.matches('.ai-overview-action-btn--undo, .ai-overview-action-btn--redo')
  ))],
});

// DOM order follows message chronology; mounted history must not outrank newer controls.
// eslint-disable-next-line max-statements
export const discoverActionMirrorSource = (root: HTMLElement): ActionMirrorSource | null => {
  const bars = regionBars(root);
  let latest: ActionMirrorSource | null = null;
  let passive: ActionMirrorSource | null = null;

  for (let index = bars.length - 1; index >= 0; index -= 1) {
    const source = readSource(bars[index]);

    // The controller that received a rejection owns recovery, irrespective of
    // newer transcript entries or the labels/types of their buttons.
    if (source?.retryable) return source;
    if (!source || latest) continue;
    if (isPassiveSource(source)) passive ??= source;
    else if (!passive) latest = source;
    // A side action (Export Proposal, Share…) leaves a receipt offering only
    // Undo/Share; it must not take the dock from the gate it did not answer.
    else latest = source.element.dataset.actionDecisionOpen === 'true' ? withNewerUtilities(source, passive) : passive;
  }

  return latest ?? passive;
};
interface RegionOptions { projectId?: string | null; sessionId?: string | null; enabled: boolean; priority?: number }
// The observers and their teardown share one lifecycle.
// eslint-disable-next-line max-statements
export const observeActionMirrorRegion = (element: HTMLElement, options: RegionOptions): (() => void) => {
  const token = Symbol('action-mirror-region');
  const scope = actionMirrorScope(options.projectId, options.sessionId);
  const refresh = () => updateMirrorRegion(token, {
    scope, priority: options.priority ?? 0, source: discoverActionMirrorSource(element), bars: regionBars(element),
    readSource: () => discoverActionMirrorSource(element),
  });
  const observer = new MutationObserver((changes) => {
    if (changes.every((change) => (change.target.nodeType === Node.ELEMENT_NODE
      ? change.target as Element : change.target.parentElement)?.closest(MIRROR))) return;
    refresh();
  });

  observer.observe(element, { subtree: true, childList: true, characterData: true, attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'disabled', 'aria-disabled', 'aria-busy', 'aria-pressed', 'aria-label',
      'aria-hidden', 'title', 'data-awaiting-user', 'data-action-inline-tail', 'data-action-mirror-owner',
      'data-action-owner-label', 'data-action-source-entry-id', 'data-attention-primary', 'data-action-placement',
      'data-action-attention-active', 'data-action-retryable', 'data-action-decision-open'] });
  const ancestors = new MutationObserver(refresh);
  let parent = element.parentElement;

  while (parent) {
    ancestors.observe(parent, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
    parent = parent.parentElement;
  }
  window.addEventListener('resize', refresh);
  refresh();

  return () => {
    observer.disconnect(); ancestors.disconnect(); window.removeEventListener('resize', refresh);
    removeMirrorRegion(token);
  };
};
/** A conditional timeline can mount its DOM after the initial loading render. */
export const useActionMirrorRegion = (root: RefObject<HTMLElement | null>, options: RegionOptions): void => {
  const registration = useRef<{ element: HTMLElement; key: string; dispose: () => void } | null>(null);
  const key = JSON.stringify([options.projectId, options.sessionId ?? null, options.enabled, options.priority ?? 0]);

  useLayoutEffect(() => {
    const element = root.current;

    if (registration.current?.element === element && registration.current.key === key) return;
    registration.current?.dispose();
    registration.current = element && options.enabled && options.projectId ? {
      element, key, dispose: observeActionMirrorRegion(element, options),
    } : null;
  });
  useLayoutEffect(() => () => { registration.current?.dispose(); registration.current = null; }, []);
};
